import DOMPurify from "dompurify";

/**
 * PRD 024 §6 / §7 #4 / 021 §10 — render-time HTML sanitizer, promoted to
 * `@juristech/web-shared` (web-customer-communications plan §4) so both
 * admin-web and web share one sanitization boundary.
 *
 * The single authoritative sanitization boundary for thread message
 * bodies. The admin composer emits UNSANITIZED `bodyHtml` on send; every
 * message — newly composed, persisted before this work, or from any
 * producer — is neutralized here on the way to the DOM. Sanitizing on
 * output (never trusting stored HTML) means the allowlist below is the
 * only place dangerous markup is removed.
 *
 * Deep, isolated module: large behavior (the full DOMPurify allowlist)
 * behind a small pure interface, testable without an Angular harness.
 */

/**
 * Rendering audience for a sanitized thread body. Controls whether staff
 * mention metadata (`data-user-id` and other `data-*` attributes)
 * survives sanitization.
 */
export enum ThreadSanitizerVariant {
  /**
   * Staff-facing admin-web bubble: mention spans keep `data-user-id`
   * so the panel can resolve staff mentions.
   */
  Admin = "admin",
  /**
   * Customer-facing apps/web bubble: `data-user-id` (and every other
   * `data-*` attribute) is stripped so internal staff userIds never
   * reach a customer's DOM.
   */
  Customer = "customer",
}

/**
 * Tags kept (021 §10). Everything else — `script`, `style`, `iframe`, and
 * any tag not on this list — is stripped, with its text content
 * preserved per DOMPurify's default `KEEP_CONTENT` behavior.
 */
const ALLOWED_TAGS: readonly string[] = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "span",
];

/**
 * Attributes kept for every variant (021 §10):
 * - `href` on `a` (protocol-filtered to http/https/mailto below),
 * - `class` on mention spans,
 * - `style` on `span` (color / background-color only — enforced by the
 *   `uponSanitizeAttribute` hook below).
 */
const BASE_ALLOWED_ATTR: readonly string[] = ["href", "class", "style"];

/**
 * Mention-span staff-identity attribute. Admin-only: kept for
 * {@link ThreadSanitizerVariant.Admin}, stripped for
 * {@link ThreadSanitizerVariant.Customer}.
 */
const MENTION_USER_ID_ATTR = "data-user-id";

/**
 * Inline-style allowlist: only `color` and `background-color`
 * declarations survive on a `span`. Any other property (e.g.
 * `position`, `display`, `behavior`) is stripped so a style attribute
 * cannot be used as an attack vector. The value permits `rgb()` / `rgba()`
 * function notation (the palettes emit `rgb(...)`) but forbids `{`, `}`,
 * and the `url(` / `expression(` vectors via the negative lookahead.
 */
const SAFE_STYLE_DECLARATION =
  /^\s*(color|background-color)\s*:\s*(?!.*(?:url|expression|javascript)\s*\()[^;{}]+;?\s*$/i;

let hookRegistered = false;

/**
 * Register the per-instance attribute hook exactly once. The hook narrows
 * the `style` attribute to color/background-color declarations on `span`
 * only, dropping it everywhere else and dropping any unsafe declaration.
 */
function ensureStyleHook(): void {
  if (hookRegistered) return;
  hookRegistered = true;
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName !== "style") return;
    const isSpan = node.nodeName.toLowerCase() === "span";
    const declarations = data.attrValue
      .split(";")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    const allSafe =
      declarations.length > 0 &&
      declarations.every((decl) => SAFE_STYLE_DECLARATION.test(decl));
    if (!isSpan || !allSafe) {
      data.keepAttr = false;
    }
  });
}

/**
 * Sanitize a stored/composed message `bodyHtml` into a safe HTML string
 * for trusted binding in a thread bubble.
 *
 * Defaults to the {@link ThreadSanitizerVariant.Customer} variant —
 * the stricter allowlist that strips staff mention metadata — so a
 * consumer that forgets to pick a variant can never leak internal
 * userIds to a customer. Admin-web must opt in to
 * {@link ThreadSanitizerVariant.Admin} explicitly.
 *
 * @param html - Raw `bodyHtml` from a message row; may be `null` /
 *   `undefined` for plain-text-only rows.
 * @param variant - Rendering audience; defaults to
 *   {@link ThreadSanitizerVariant.Customer}.
 * @returns Sanitized HTML containing only allowlisted tags / attributes;
 *   empty string when the input is nullish or empty.
 *
 * @example
 * sanitizeThreadBodyHtml('<p>hi</p><script>x()</script>'); // '<p>hi</p>'
 * sanitizeThreadBodyHtml(html, ThreadSanitizerVariant.Admin); // keeps data-user-id
 */
export function sanitizeThreadBodyHtml(
  html: string | null | undefined,
  variant: ThreadSanitizerVariant = ThreadSanitizerVariant.Customer,
): string {
  if (!html) return "";
  ensureStyleHook();
  const allowMentionUserId = variant === ThreadSanitizerVariant.Admin;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: allowMentionUserId
      ? [...BASE_ALLOWED_ATTR, MENTION_USER_ID_ATTR]
      : [...BASE_ALLOWED_ATTR],
    // Allow only safe URL schemes on href; `javascript:` / `data:` are
    // rejected because they are absent from this regex.
    ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
    // Admin: mention spans carry `data-user-id`. data-* attributes are
    // inert (no script execution), so allowing them is safe there.
    // Customer: all data-* attributes are dropped so staff identity
    // metadata never reaches a customer's DOM.
    ALLOW_DATA_ATTR: allowMentionUserId,
  });
}
